import { Button } from '@repo/ui/src/components/ui/button';
import { DetailSkeleton } from '../../components/DetailSkeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/src/components/ui/tooltip';
import { cn } from '@repo/ui/src/lib/utils';
import { formatYmd, parseYmd } from '@repo/ui/src/lib/dateStrings';
import { ArrowLeft, Plus, Trash } from 'lucide-react';
import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ClientCombobox } from '../../components/ClientCombobox';
import { DatePicker } from '../../components/pickers/DatePicker';
import { PageHeader } from '../../components/PageHeader';
import { useClientOptions } from '../../hooks/useClients';
import { useCreateInvoice, useInvoice, useUpdateInvoice } from '../../hooks/useInvoices';
import { useChatStore } from '../../stores/chatStore';

/** Numeric fields: focusing selects the current value so the next keystroke
 *  replaces it instead of appending to a leading 0 ("05"). */
const selectAllOnFocus = (e: FocusEvent<HTMLInputElement>) => e.currentTarget.select();

export function InvoiceFormPage() {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { data: clients, isLoading: isLoadingClients } = useClientOptions();
  const { data: existingInvoice, isLoading: isLoadingInvoice } = useInvoice(
    isEditMode ? (id as string) : null,
  );
  const { mutate: createInvoice, isPending: isCreating } = useCreateInvoice();
  const { mutate: updateInvoice, isPending: isUpdating } = useUpdateInvoice(id ?? '');
  const isPending = isEditMode ? isUpdating : isCreating;

  const hydratedRef = useRef(false);
  const initialHydration = useChatStore.getState().pendingHydration;
  const [searchParams] = useSearchParams();
  // Preselect the payer when arriving from a customer's page (13 §3). Chat
  // hydration still wins: a draft the assistant built already names its client,
  // and edit mode overwrites this from the invoice itself in the effect below.
  const presetClientId = searchParams.get('clientId') ?? '';

  const [formData, setFormData] = useState(() => {
    if (initialHydration && !hydratedRef.current) {
      hydratedRef.current = true;
      const { invoiceDraft } = initialHydration;
      useChatStore.getState().setPendingHydration(null);
      return {
        clientId: invoiceDraft.clientId ?? '',
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        taxRate: invoiceDraft.taxRate ?? 0,
        depositAmount: invoiceDraft.depositAmount ?? 0,
        notes: invoiceDraft.notes ?? '',
      };
    }
    return {
      clientId: presetClientId,
      dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      taxRate: 0,
      depositAmount: 0,
      notes: '',
    };
  });

  const [items, setItems] = useState<
    Array<{
      id: number;
      dbId?: string;
      description: string;
      quantity: number;
      unitPrice: number;
      fromClosing?: boolean;
    }>
  >(() => {
    if (initialHydration?.invoiceDraft.items?.length) {
      return initialHydration.invoiceDraft.items.map((item, index) => ({
        id: Date.now() + index,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      }));
    }
    return [{ id: 1, description: '', quantity: 1, unitPrice: 0 }];
  });

  // Edit mode: prefill client/items/dates once the invoice loads. dbId (the
  // real InvoiceItem id) is preserved per item so PUT can update rows in
  // place — required so BillingPeriod.invoiceItemId (chốt-sổ linkage) isn't
  // silently dropped when a chốt-sổ tuition invoice is edited.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || !existingInvoice || prefilledRef.current) return;
    prefilledRef.current = true;

    if (existingInvoice.status !== 'DRAFT') {
      toast.error(t('form.editLockedToast'));
      navigate(`/invoices/${existingInvoice.id}`, { replace: true });
      return;
    }

    setFormData({
      clientId: existingInvoice.clientId,
      dueDate: existingInvoice.dueDate
        ? new Date(existingInvoice.dueDate)
        : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      // API serializes Prisma Decimal as a string — coerce to number so the
      // update payload matches the numeric schema (else Zod "expected number").
      taxRate: Number(existingInvoice.taxRate),
      depositAmount: Number(existingInvoice.depositAmount),
      notes: existingInvoice.notes ?? '',
    });
    setItems(
      existingInvoice.items.map((item, index) => ({
        id: Date.now() + index,
        dbId: item.id,
        description: item.description,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        fromClosing: Boolean(item.fromClosing),
      })),
    );
  }, [isEditMode, existingInvoice, navigate, t]);

  // A `?clientId=` from a stale link (the customer was deleted, or the id is
  // just wrong) must not leave a payer selected that the picker can't show —
  // wait for the client list to actually load (useClientOptions defaults its
  // `data` to `[]` while loading, so this can't key off truthiness of
  // `clients` itself) before deciding the preset id is bogus and clearing it.
  // Edit mode never runs this: its own effect above is the source of truth
  // for clientId there.
  useEffect(() => {
    if (!presetClientId || isEditMode || isLoadingClients) return;
    if (!clients.some((c) => c.id === presetClientId)) {
      setFormData((p) => (p.clientId === presetClientId ? { ...p, clientId: '' } : p));
    }
  }, [presetClientId, isEditMode, isLoadingClients, clients]);

  const handleAddItem = () => {
    setItems([...items, { id: Date.now(), description: '', quantity: 1, unitPrice: 0 }]);
  };

  const handleRemoveItem = (id: number) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleItemChange = (id: number, field: string, value: string | number) => {
    setItems(items.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const subTotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const taxAmount = (subTotal * formData.taxRate) / 100;
  const total = subTotal + taxAmount;
  // A deduction line (negative quantity) bigger than the rest of the invoice
  // makes the total negative. The server refuses this (NegativeInvoiceTotalError,
  // see invoices.ts) — catch it here instead, where the number is visible, so
  // the teacher sees why before submitting rather than after a toast.
  const isNegativeTotal = total < 0;

  // Mirrors the API guard exactly (PUT /invoices/:id, apps/api/src/routes/invoices.ts):
  // a clientId change is only ever accepted on a DRAFT invoice where every item
  // is hand-made (isGenerated: false — `fromClosing` here is the same column,
  // remapped by the detail endpoint). That API check is the source of truth;
  // this only decides whether the field is offered, never whether a change is
  // allowed. On create there is no existing invoice to guard, so it's always on.
  const hasClosingItems = isEditMode && (existingInvoice?.items ?? []).some((item) => item.fromClosing);
  const clientFieldEnabled =
    !isEditMode ||
    (existingInvoice?.status === 'DRAFT' && (existingInvoice?.items ?? []).every((item) => !item.fromClosing));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) {
      toast.error(t('form.selectClientAlert'));
      return;
    }
    if (isNegativeTotal) {
      toast.error(t('form.negativeTotalError'));
      return;
    }

    if (isEditMode) {
      updateInvoice(
        {
          // Only when the field is editable (DRAFT, all-manual — the API guard's
          // own rule). The picker updates formData.clientId, but the payload
          // never carried it, so a changed payer silently saved as unchanged:
          // the exact bug Johnny hit. Not sent unconditionally, so a chốt-sổ
          // invoice's PUT stays clientId-free and never trips the guard.
          ...(clientFieldEnabled ? { clientId: formData.clientId } : {}),
          dueDate: formData.dueDate.toISOString(),
          taxRate: formData.taxRate,
          depositAmount: formData.depositAmount,
          notes: formData.notes,
          // Only send MANUAL items — chốt-sổ (fromClosing) lines are system-owned
          // and preserved server-side. Sending them would also fail validation
          // (e.g. the bù-trừ line's quantity is -1, which the item schema rejects).
          items: items
            .filter((item) => !item.fromClosing)
            .map((item) => ({
              id: item.dbId,
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
            })),
        },
        {
          onSuccess: () => navigate(`/invoices/${id}`),
        },
      );
      return;
    }

    createInvoice(
      {
        clientId: formData.clientId,
        dueDate: formData.dueDate.toISOString(),
        taxRate: formData.taxRate,
        depositAmount: formData.depositAmount,
        notes: formData.notes,
        items: items.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
      },
      {
        onSuccess: () => navigate('/invoices'),
      },
    );
  };

  if (isEditMode && isLoadingInvoice) {
    return <DetailSkeleton rows={2} />;
  }
  if (isEditMode && !existingInvoice) {
    return (
      <div className="p-4 sm:p-8 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">{t('detail.notFound')}</h1>
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('detail.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto w-full">
      {/* PageHeader for back + title only. The page keeps its own footer
          actions: the submit is a form-scoped type="submit" the header cannot
          host, and Huỷ belongs next to it. `size="md"` matches the page's own
          max-w-4xl. The title now truncates instead of wrapping at 390. */}
      <PageHeader
        size="md"
        title={t(isEditMode ? 'form.titleEdit' : 'form.titleExtra')}
        back={{ label: tc('back'), onClick: () => navigate(-1) }}
      />

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Details */}
        <Card>
          <CardHeader>
            <CardTitle>{t('form.invoiceDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="clientId">{t('form.client')}</Label>
              <ClientCombobox
                options={clients ?? []}
                value={formData.clientId}
                onSelect={(clientId) => setFormData({ ...formData, clientId })}
                disabled={!clientFieldEnabled}
                placeholder={t('form.selectClient')}
                searchPlaceholder={t('form.clientSearchPlaceholder')}
                emptyText={t('form.clientEmpty')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('form.dueDate')}</Label>
              {/* formData.dueDate stays a real Date internally (toISOString()
                  feeds the create/update payload below) — the picker only sees
                  its 'YYYY-MM-DD' string at this boundary. */}
              <DatePicker
                className="w-full"
                value={formatYmd(formData.dueDate)}
                onChange={(next) => {
                  const parsed = parseYmd(next);
                  if (parsed) setFormData({ ...formData, dueDate: parsed });
                }}
                placeholder={t('form.selectDate')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <CardTitle className="min-w-0 truncate">{t('form.lineItems')}</CardTitle>
            {/* Icon-only on phones so the heading keeps its line; the label
                returns at sm. 44px pinned — --spacing is 0.2rem, so size="sm"
                alone lands well under a usable target. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddItem}
              aria-label={t('form.addItem')}
              className="min-h-[44px] min-w-[44px] shrink-0 gap-2 sm:min-h-0 sm:min-w-0"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('form.addItem')}</span>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Table-style header row — labels live here ONCE, not per input row, so
                every item row (and its trash button) aligns via items-center. */}
            <div className="hidden gap-4 items-center text-sm font-medium text-muted-foreground sm:flex">
              <div className="flex-1">{t('form.description')}</div>
              <div className="w-24">{t('form.qty')}</div>
              <div className="w-32">{t('form.unitPrice')}</div>
              <div className="w-10 shrink-0" aria-hidden />
            </div>
            {/* One-line hint: a negative quantity turns the line into a deduction
                (e.g. tuition already collected in cash before onboarding). */}
            <p className="text-xs text-muted-foreground">{t('form.negativeQtyHint')}</p>
            {/* A hand-added line on a chốt-sổ invoice is invisible to next month's
                bù trừ. A teacher patched a wrong-schedule bill with one (prod,
                2026-10-01) and the next month charged the same sessions again. */}
            {hasClosingItems && (
              <p className="rounded-md border border-attention-tint bg-attention-tint/40 px-3 py-2 text-xs leading-snug text-attention-foreground">
                {t('form.manualOnClosingHint')}
              </p>
            )}
            {items.map((item) => {
              const locked = Boolean(item.fromClosing);
              return (
                // ONE tree, reflowed — not a phone card beside a desktop row.
                // These Inputs are `required`, and a display:none required
                // control still takes part in constraint validation: the
                // browser would refuse to submit with "not focusable". So the
                // row stacks instead of duplicating. `sm:contents` dissolves
                // the numeric group at ≥640 so its children become flex items
                // of the outer row again — the exact pre-change layout.
                <div
                  key={item.id}
                  className="flex flex-col gap-2 rounded-md border border-surface-200 p-3 sm:flex-row sm:items-center sm:gap-4 sm:rounded-none sm:border-0 sm:p-0"
                >
                  <div className="sm:flex-1">
                    <Label htmlFor={`item-desc-${item.id}`} className="mb-1 block text-xs text-muted-foreground sm:sr-only">
                      {t('form.description')}
                    </Label>
                    <Input
                      id={`item-desc-${item.id}`}
                      placeholder={t('form.descriptionPlaceholder')}
                      value={item.description}
                      onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                      disabled={locked}
                      required
                    />
                  </div>
                  <div className="flex items-end gap-2 sm:contents">
                    <div className="flex-1 sm:w-24 sm:flex-none">
                      <Label htmlFor={`item-qty-${item.id}`} className="mb-1 block text-xs text-muted-foreground sm:sr-only">
                        {t('form.qty')}
                      </Label>
                      <Input
                        id={`item-qty-${item.id}`}
                        type="number"
                        // No `min`: quantity may be negative (a deduction line,
                        // see the hint above) — only zero is invalid, which the
                        // server enforces (HTML min/step can't express "any
                        // nonzero int").
                        value={item.quantity}
                        onChange={(e) => handleItemChange(item.id, 'quantity', Number(e.target.value))}
                        onFocus={selectAllOnFocus}
                        disabled={locked}
                        required
                      />
                    </div>
                    <div className="flex-1 sm:w-32 sm:flex-none">
                      <Label htmlFor={`item-price-${item.id}`} className="mb-1 block text-xs text-muted-foreground sm:sr-only">
                        {t('form.unitPrice')}
                      </Label>
                      <Input
                        id={`item-price-${item.id}`}
                        type="number"
                        min="0"
                        // 0 is the "nothing entered yet" default, so show an empty
                        // box instead of a 0 the teacher has to delete; '' parses
                        // back to 0. Focus selects any existing value so typing
                        // replaces it (the standard numeric-field behaviour).
                        value={item.unitPrice === 0 ? '' : item.unitPrice}
                        placeholder="0"
                        onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value === '' ? 0 : Number(e.target.value))}
                        onFocus={selectAllOnFocus}
                        disabled={locked}
                        required
                      />
                    </div>
                    <div className="flex w-10 shrink-0 justify-center">
                      {locked ? (
                        // Chốt-sổ item: styled-disabled button (NOT the `disabled` attr, so
                        // hover still shows the tooltip); no onClick → does nothing.
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground opacity-50 cursor-not-allowed"
                                aria-disabled
                                aria-label={t('form.fromClosingTooltip')}
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[280px] text-xs leading-snug">
                              {t('form.fromClosingTooltip')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={items.length === 1}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Phones lose the aligned columns, so the line's own money
                      is spelled out; at sm the column arithmetic is visible in
                      the row itself and this would be noise. */}
                  <p className="text-xs text-muted-foreground sm:hidden">
                    {t('form.lineTotal')}:{' '}
                    <span className="tabular-nums text-surface-900">
                      {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                        item.quantity * item.unitPrice,
                      )}
                    </span>
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Totals & Notes */}
        <div className="grid gap-8 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('form.additionalInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="notes">{t('form.notes')}</Label>
                <textarea
                  id="notes"
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={t('form.notesPlaceholder')}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('form.summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm py-1">
                <span>{t('form.subtotal')}</span>
                <span>
                  {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                    subTotal,
                  )}
                </span>
              </div>

              <div className="flex items-center gap-4 py-1">
                <span className="text-sm">{t('form.taxRate')}</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  className="w-20 ml-auto h-8"
                  value={formData.taxRate === 0 ? '' : formData.taxRate}
                  placeholder="0"
                  onChange={(e) => setFormData({ ...formData, taxRate: e.target.value === '' ? 0 : Number(e.target.value) })}
                  onFocus={selectAllOnFocus}
                />
              </div>

              {taxAmount > 0 && (
                <div className="flex justify-between text-sm py-1 text-muted-foreground">
                  <span>{t('form.taxAmount')}</span>
                  <span>
                    {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                      taxAmount,
                    )}
                  </span>
                </div>
              )}

              <div className="border-t pt-4 flex justify-between font-bold text-lg">
                <span>{t('form.total')}</span>
                <span className={cn(isNegativeTotal && 'text-destructive')}>
                  {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                    total,
                  )}
                </span>
              </div>
              {isNegativeTotal && (
                <p className="text-sm text-destructive">{t('form.negativeTotalError')}</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col-reverse gap-3 pb-12 sm:flex-row sm:justify-end sm:gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(isEditMode ? `/invoices/${id}` : '/invoices')}
            className="w-full min-h-[44px] sm:w-auto sm:min-h-0"
          >
            {t('form.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isPending || isNegativeTotal}
            className="w-full min-h-[44px] sm:w-auto sm:min-h-0"
          >
            {isEditMode
              ? isPending
                ? t('form.savingEdit')
                : t('form.submitEdit')
              : isPending
                ? t('form.submitting')
                : t('form.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
}
